import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { common } from '@mui/material/colors';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Grid2';
import { Theme, useMediaQuery } from '@mui/material';

import SearchFilterBar, {
  QuickFiltersType,
} from 'src/components/SearchFilterBar';
import {
  useGetDispoTimeWindows,
  useGetJobApplicationsCount,
} from 'src/pages/ProjectDetailsPage/api';
import { JOB_MODE } from 'src/utils/jobMode';

import messages from './messages';
import StyledTabs from 'src/components/StyledTabs/StyledTabs';
import StyledTab from 'src/components/StyledTab/StyledTab';
import JobApplicationsAll from '../JobApplicationsAll/JobApplicationsAll';
import JobApplicationsApplied from '../JobApplicationsApplied/JobApplicationsApplied';
import JobApplicationsAccepted from '../JobApplicationsAccepted/JobApplicationsAccepted';
import JobApplicationsBooked from '../JobApplicationsBooked/JobApplicationsBooked';
import JobApplicationsDenied from '../JobApplicationsDenied/JobApplicationsDenied';
import JobApplicationsDone from '../JobApplicationsDone/JobApplicationsDone';
import JobDateFilter from '../JobDateFilter/JobDateFilter';

const JobApplicationsComponents = [
  JobApplicationsAll,
  JobApplicationsApplied,
  JobApplicationsAccepted,
  JobApplicationsBooked,
  JobApplicationsDenied,
  JobApplicationsDone,
];

const COUNT_TYPES = [
  'all_count',
  'applied_count',
  'accepted_count',
  'booked_count',
  'denied_count',
  'done_count',
];

export default function MainJob({
  job,
  uuid,
  candidate,
  calendarfilters,
  onCandidateSelect,
}: UCM.JobTypeProps) {
  const { formatMessage } = useIntl();

  const [tab, setTab] = useState(0);
  const [filters, setFilters] = useState<Record<string, string>>({});
  const isDispo = job?.jobMode === JOB_MODE.DISPO && uuid === job.uuid;

  const quickFilters: Array<QuickFiltersType> = useRef([
    {
      autoFocus: false,
      id: 'quick_search',
      type: 'text' as QuickFiltersType['type'],
      placeholder: formatMessage(messages.jobSerchLabel),
    },
  ]).current;

  const { counts } = useGetJobApplicationsCount({
    jobUuid: uuid ?? '',
    types: COUNT_TYPES,
    filters,
    enabled: Boolean(uuid),
  });

  const updateFilters = useCallback((updates: Record<string, any>) => {
    setFilters((prevState) => ({ ...prevState, ...updates }));
  }, []);

  useEffect(() => {
    updateFilters({
      ...calendarfilters,
      job_date_id: calendarfilters.job_date_id ?? '',
    });
  }, [calendarfilters, updateFilters]);

  const { data: timeWindows } = useGetDispoTimeWindows({
    jobUuid: uuid ?? '',
    shiftDate: filters.shift_date,
    enabled: isDispo && Boolean(filters.shift_date),
  });
  const hasSeveralTimeWindows =
    (timeWindows?.filter(({ startsAt }) => startsAt).length ?? 0) > 1;

  useEffect(() => {
    if (
      filters.job_date_id &&
      timeWindows &&
      !timeWindows.some(({ id }) => id === filters.job_date_id)
    ) {
      updateFilters({ job_date_id: '' });
    }
  }, [filters.job_date_id, timeWindows, updateFilters]);

  const changeTab = useCallback((_: React.SyntheticEvent, newValue: number) => {
    setTab(newValue);
  }, []);

  const TabData = useMemo(
    () => [
      { label: formatMessage(messages.allTabLabel), badge: counts?.all_count },
      {
        label: formatMessage(messages.appliedTabLabel),
        badge: counts?.applied_count,
      },
      {
        label: formatMessage(messages.acceptedTabLabel),
        badge: counts?.accepted_count,
      },
      {
        label: formatMessage(messages.bookedTabLabel),
        badge: counts?.booked_count,
      },
      {
        label: formatMessage(messages.deniedTabLabel),
        badge: counts?.denied_count,
      },
      {
        label: formatMessage(messages.doneTabLabel),
        badge: counts?.done_count,
      },
    ],
    [counts],
  );

  const CurrentJobComponent = useMemo(() => {
    return JobApplicationsComponents[tab];
  }, [tab]);

  const large = useMediaQuery((theme: Theme) => theme.breakpoints.up('lg'));

  return (
    <>
      {isDispo && timeWindows && hasSeveralTimeWindows && (
        <JobDateFilter
          timeWindows={timeWindows}
          selectedJobDateId={filters.job_date_id ?? ''}
          onSelect={(jobDateId) => updateFilters({ job_date_id: jobDateId })}
        />
      )}
      <Stack
        display={'flex'}
        flexDirection={large ? 'row' : 'column'}
        justifyContent={large ? 'space-between' : undefined}
        alignItems={large ? 'center' : undefined}
        gap={2}
        px={2}
        pb={2}
        bgcolor={common.white}
      >
        <StyledTabs value={tab} onChange={changeTab}>
          {TabData.map((tabItem, index) => (
            <StyledTab
              key={index}
              label={tabItem.label}
              badge={tabItem?.badge}
            />
          ))}
        </StyledTabs>
        <Grid size={{ xs: 12, sm: 7, md: 5, lg: 3 }} alignSelf="flex-end">
          <SearchFilterBar fields={quickFilters} onSearch={updateFilters} />
        </Grid>
      </Stack>
      <CurrentJobComponent
        key={
          isDispo
            ? `${uuid ?? ''}:${filters.shift_date ?? ''}:${filters.job_date_id ?? ''}`
            : undefined
        }
        job={job}
        jobUuid={uuid!}
        filters={filters}
        candidate={candidate}
        onCandidateSelect={onCandidateSelect}
      />
    </>
  );
}
